/**
 * Componente principal de Control Semanal - Versión Refactorizada
 * Este componente orquesta la visualización semanal de control de guías
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import SimpleControlGestionLayout from '../../components/SimpleControlGestionLayout';
import { useGuias } from '../../../Mantenedores/hooks/useGuias';
import { useEstadosTiposYFiltrado } from '../../../Mantenedores/hooks/useEstadosTiposYFiltrado';
import * as FaIcons from 'react-icons/fa';
import Swal from 'sweetalert2';

// Importar componentes
import DateNavigator from './components/DateNavigator';
import WeeklyTable from './components/WeeklyTable';
import WeeklyChart from './components/WeeklyChart';
import DetailModal from './components/DetailModal';
import ExcelFormatModal from './components/ExcelFormatModal';
import ReconciliationModal from './components/ReconciliationModal';
import SubestadoModal from './components/SubestadoModal';

// Importar utilidades
import {
  calculateInitialDateRange,
  getDateColumns as getDateColumnsHelper,
  processWeeklyData,
  canNavigateNext,
  navigateDates,
  getDateTotal
} from './utils/helpers';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_NUMBER } from './utils/constants';

// Importar estilos
import './ControlSemanal.css';

const ControlSemanal = () => {
  const { user: currentUser } = useSelector(state => state.auth);
  
  // Hooks personalizados
  const {
    loading,
    error,
    success,
    dailyReception,
    handleGetDailyReception,
    handleGetOrphanItems,
    handleFindMatchesForItems,
    handleReconcileItems,
    handleUpdateSubestadosBulk,
    handleExportDailyReceptionExcel,
    matches,
    reconciliationResult
  } = useGuias();

  const { loadEstadosByTipo } = useEstadosTiposYFiltrado();

  // ========== ESTADO ==========
  // Rango de fechas (calculado una sola vez con useMemo)
  const initialDateRange = useMemo(() => calculateInitialDateRange(), []);
  const [dateRange, setDateRange] = useState(initialDateRange);

  // Datos procesados
  const [weeklyData, setWeeklyData] = useState({});
  const [allStates, setAllStates] = useState([]);

  // Modal de detalles
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedGuias, setSelectedGuias] = useState({});

  // Modal de Excel
  const [showExcelFormatModal, setShowExcelFormatModal] = useState(false);
  const [selectedExcelFormat, setSelectedExcelFormat] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentExportParams, setCurrentExportParams] = useState(null);

  // Modal de reconciliación
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState({});
  const [orphanItemsForReconciliation, setOrphanItemsForReconciliation] = useState([]);

  // Modal de subestados
  const [showSubestadoModal, setShowSubestadoModal] = useState(false);
  const [subestadoLoading, setSubestadoLoading] = useState(false);
  const [estadosData, setEstadosData] = useState([]);
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [selectedSubestado, setSelectedSubestado] = useState(null);

  // ========== EFECTOS ==========
  // Cargar datos cuando cambian las fechas
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      loadWeeklyData(dateRange);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // ========== FUNCIONES DE CARGA ==========
  const loadWeeklyData = async (range) => {
    const params = {
      fecha_inicio: range.startDate,
      fecha_fin: range.endDate,
      detalles: false
    };

    const response = await handleGetDailyReception(params);
    if (response.success && response.data) {
      const { processedData, states } = processWeeklyData(response.data);
      setWeeklyData(processedData);
      setAllStates(states);
    }
  };

  // ========== HANDLERS DE NAVEGACIÓN ==========
  const handleNavigateDate = (direction) => {
    const newRange = navigateDates(dateRange, direction);
    if (newRange) {
      setDateRange(newRange);
    }
  };

  // ========== HANDLERS DE TABLA ==========
  const handleCellClick = async (date, state) => {
    const count = weeklyData[date]?.[state] || 0;
    if (count === 0) return;

    setSelectedCell({ date, state, count });
    setShowDetailModal(true);
    setDetailLoading(true);
    setSelectedGuias({});

    try {
      let response;

      if (state === 'Sin estado') {
        const params = {
          fecha_inicio: date,
          fecha_fin: date,
          pageSize: DEFAULT_PAGE_SIZE,
          pageNumber: DEFAULT_PAGE_NUMBER
        };
        setCurrentExportParams(null);
        response = await handleGetOrphanItems(params);
      } else {
        const params = {
          fecha_inicio: date,
          fecha_fin: date,
          estado: state,
          detalles: true
        };
        
        setCurrentExportParams({
          fecha_inicio: date,
          fecha_fin: date,
          estado: state
        });
        
        response = await handleGetDailyReception(params);
      }

      if (response.success) {
        let processedData = [];

        if (state === 'Sin estado') {
          processedData = response.data.data || [];
        } else {
          if (response.data && response.data.length > 0) {
            response.data.forEach(day => {
              if (day.subestados && day.subestados.length > 0) {
                day.subestados.forEach(subestado => {
                  if (subestado.guias && subestado.guias.length > 0) {
                    processedData.push(...subestado.guias);
                  }
                });
              }
            });
          }
        }

        setDetailData(processedData);
      } else {
        setDetailData([]);
      }
    } catch (error) {
      console.error('Error al cargar detalles:', error);
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDayTotalClick = async (date) => {
    const count = getDateTotal(weeklyData, date);
    if (count === 0) return;

    setSelectedCell({ date, state: 'Todas las guías', count });
    setShowDetailModal(true);
    setDetailLoading(true);
    setSelectedGuias({});

    try {
      const params = {
        fecha_inicio: date,
        fecha_fin: date,
        detalles: true
      };
      
      setCurrentExportParams({
        fecha_inicio: date,
        fecha_fin: date
      });
      
      const response = await handleGetDailyReception(params);

      if (response.success) {
        let processedData = [];

        if (response.data && response.data.length > 0) {
          response.data.forEach(day => {
            if (day.subestados && day.subestados.length > 0) {
              day.subestados.forEach(subestado => {
                if (subestado.guias && subestado.guias.length > 0) {
                  processedData.push(...subestado.guias);
                }
              });
            }
          });
        }

        setDetailData(processedData);
      } else {
        setDetailData([]);
      }
    } catch (error) {
      console.error('Error al cargar detalles del día:', error);
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // ========== HANDLERS DE MODAL DE DETALLES ==========
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setDetailData([]);
    setSelectedCell(null);
    setSelectedGuias({});
    setCurrentExportParams(null);
  };

  const handleGuiaSelection = (guiaId, selected) => {
    setSelectedGuias(prev => ({
      ...prev,
      [guiaId]: selected
    }));
  };

  const handleSelectAllGuias = (selected) => {
    const newSelection = {};
    detailData.forEach(item => {
      if (item.guia_id) {
        newSelection[item.guia_id] = selected;
      }
    });
    setSelectedGuias(newSelection);
  };

  // ========== HANDLERS DE EXCEL ==========
  const handleOpenExcelModal = () => {
    if (!currentExportParams) return;
    setShowExcelFormatModal(true);
    setSelectedExcelFormat(null);
  };

  const handleCloseExcelModal = () => {
    if (!exportLoading) {
      setShowExcelFormatModal(false);
      setSelectedExcelFormat(null);
    }
  };

  const handleExcelFormatSelect = (format) => {
    setSelectedExcelFormat(format);
  };

  const handleExecuteExcelExport = async () => {
    if (!currentExportParams || !selectedExcelFormat) return;

    setExportLoading(true);
    try {
      const paramsWithFormat = {
        ...currentExportParams,
        formato: selectedExcelFormat
      };

      const response = await handleExportDailyReceptionExcel(paramsWithFormat);
      
      if (response.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Excel exportado',
          text: 'El archivo se ha descargado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
        setShowExcelFormatModal(false);
        setSelectedExcelFormat(null);
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: response.error || 'Ocurrió un error al generar el Excel',
          confirmButtonText: 'Aceptar'
        });
      }
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al exportar el Excel',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setExportLoading(false);
    }
  };

  // ========== HANDLERS DE RECONCILIACIÓN ==========
  const handleStartReconciliation = async () => {
    if (!detailData.length) return;

    setReconciliationLoading(true);
    setOrphanItemsForReconciliation(detailData);

    const itemIds = detailData.map(item => item.item_id);

    try {
      const response = await handleFindMatchesForItems({ item_ids: itemIds });

      if (response.success && response.data.matches) {
        const hasMatches = response.data.summary?.matches_found > 0;

        if (!hasMatches) {
          await Swal.fire({
            icon: 'info',
            title: 'Sin matches',
            text: 'No se encontraron coincidencias favorables para los ítems seleccionados',
            confirmButtonText: 'Entendido'
          });
          setReconciliationLoading(false);
          return;
        }

        // Inicializar selección de matches
        const initialSelection = {};
        response.data.matches.forEach(match => {
          if (match.guia_match) {
            initialSelection[match.item_id] = true;
          }
        });
        setSelectedMatches(initialSelection);
        setShowReconciliationModal(true);
      } else {
        console.error('Error al buscar matches:', response.error);
      }
    } catch (error) {
      console.error('Error al iniciar reconciliación:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al buscar matches para los ítems',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setReconciliationLoading(false);
    }
  };

  const handleMatchSelection = (itemId, selected) => {
    setSelectedMatches(prev => ({
      ...prev,
      [itemId]: selected
    }));
  };

  const handleSelectAllMatches = (selected) => {
    const newSelection = {};
    if (matches && matches.matches) {
      matches.matches.forEach(match => {
        if (match.guia_match) {
          newSelection[match.item_id] = selected;
        }
      });
    }
    setSelectedMatches(newSelection);
  };

  const handleExecuteReconciliation = async () => {
    const selectedItemIds = Object.keys(selectedMatches).filter(
      itemId => selectedMatches[itemId]
    );

    if (!selectedItemIds.length) {
      await Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Debe seleccionar al menos un ítem para reconciliar',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    setReconciliationLoading(true);

    try {
      const reconciliations = selectedItemIds.map(itemId => {
        const match = matches.matches.find(m => m.item_id === parseInt(itemId));
        return {
          item_id: parseInt(itemId),
          guia_id: match.guia_match.guia_id
        };
      });

      const response = await handleReconcileItems({ reconciliations });

      if (response.success) {
        setShowReconciliationModal(false);
        setSelectedMatches({});

        await loadWeeklyData(dateRange);

        await Swal.fire({
          icon: 'success',
          title: 'Reconciliación exitosa',
          text: `Se reconciliaron ${reconciliations.length} ítems correctamente`,
          timer: 3000,
          showConfirmButton: false
        });

        handleCloseDetailModal();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error en reconciliación',
          text: response.error || 'Ocurrió un error al reconciliar los ítems',
          confirmButtonText: 'Aceptar'
        });
      }
    } catch (error) {
      console.error('Error al ejecutar reconciliación:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error en reconciliación',
        text: 'Ocurrió un error al ejecutar la reconciliación',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setReconciliationLoading(false);
    }
  };

  const handleCloseReconciliationModal = () => {
    setShowReconciliationModal(false);
    setSelectedMatches({});
    setOrphanItemsForReconciliation([]);
  };

  // ========== HANDLERS DE SUBESTADOS ==========
  const handleOpenSubestadoModal = async () => {
    const selectedGuiaIds = Object.keys(selectedGuias).filter(
      guiaId => selectedGuias[guiaId]
    );

    if (!selectedGuiaIds.length) {
      await Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Debe seleccionar al menos una guía para cambiar el subestado',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    setSubestadoLoading(true);
    setShowSubestadoModal(true);

    try {
      const estados = await loadEstadosByTipo('guia');
      setEstadosData(estados);
    } catch (error) {
      console.error('Error al cargar estados:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar los estados disponibles',
        confirmButtonText: 'Aceptar'
      });
      setShowSubestadoModal(false);
    } finally {
      setSubestadoLoading(false);
    }
  };

  const handleEstadoChange = (estadoId) => {
    setSelectedEstado(estadoId);
    setSelectedSubestado(null); // Reset subestado when estado changes
  };

  const handleSubestadoChange = (subestadoId) => {
    setSelectedSubestado(subestadoId);
  };

  const handleExecuteSubestadoChange = async () => {
    if (!selectedEstado || !selectedSubestado) {
      await Swal.fire({
        icon: 'warning',
        title: 'Selección incompleta',
        text: 'Debe seleccionar un estado y un subestado',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    const selectedGuiaIds = Object.keys(selectedGuias).filter(
      guiaId => selectedGuias[guiaId]
    );

    setSubestadoLoading(true);

    try {
      const updates = selectedGuiaIds.map(guiaId => ({
        guia_id: parseInt(guiaId),
        sub_estado_id: parseInt(selectedSubestado)
      }));

      const response = await handleUpdateSubestadosBulk(updates);

      if (response.success) {
        setShowSubestadoModal(false);
        setSelectedEstado(null);
        setSelectedSubestado(null);

        await loadWeeklyData(dateRange);

        await Swal.fire({
          icon: 'success',
          title: 'Actualización exitosa',
          text: `Se actualizaron ${updates.length} guías correctamente`,
          timer: 3000,
          showConfirmButton: false
        });

        handleCloseDetailModal();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error al actualizar',
          text: response.error || 'Ocurrió un error al actualizar los subestados',
          confirmButtonText: 'Aceptar'
        });
      }
    } catch (error) {
      console.error('Error al ejecutar cambio de subestado:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al actualizar los subestados',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setSubestadoLoading(false);
    }
  };

  const handleCloseSubestadoModal = () => {
    setShowSubestadoModal(false);
    setSelectedEstado(null);
    setSelectedSubestado(null);
    setEstadosData([]);
  };

  // ========== CÁLCULOS ==========
  const dateColumns = getDateColumnsHelper(dateRange.startDate, dateRange.endDate);
  const selectedGuiasCount = Object.values(selectedGuias).filter(Boolean).length;

  // ========== RENDER ==========
  return (
    <SimpleControlGestionLayout
      title="Control Semanal"
      subtitle="Visualiza el comportamiento semanal de las entregas por estado"
      icon={<FaIcons.FaCalendarWeek />}
      iconColor="#28ac33ff"
      breadcrumbs={[{ label: 'Control Semanal' }]}
    >
      {/* Navegador de fechas */}
      <DateNavigator
        dateRange={dateRange}
        onNavigate={handleNavigateDate}
        canNavigateNext={canNavigateNext(dateRange.endDate)}
        loading={loading}
      />

      {/* Tabla semanal */}
      <WeeklyTable
        dateColumns={dateColumns}
        weeklyData={weeklyData}
        allStates={allStates}
        onCellClick={handleCellClick}
        onDayTotalClick={handleDayTotalClick}
        loading={loading}
      />

      {/* Gráfico semanal */}
      <WeeklyChart
        dateColumns={dateColumns}
        weeklyData={weeklyData}
        allStates={allStates}
      />

      {/* Modal de detalles */}
      <DetailModal
        show={showDetailModal}
        onHide={handleCloseDetailModal}
        selectedCell={selectedCell}
        detailData={detailData}
        detailLoading={detailLoading}
        selectedGuias={selectedGuias}
        onGuiaSelection={handleGuiaSelection}
        onSelectAllGuias={handleSelectAllGuias}
        onOpenSubestadoModal={handleOpenSubestadoModal}
        onStartReconciliation={handleStartReconciliation}
        onOpenExcelModal={handleOpenExcelModal}
        currentExportParams={currentExportParams}
        reconciliationLoading={reconciliationLoading}
        subestadoLoading={subestadoLoading}
      />

      {/* Modal de formato de Excel */}
      <ExcelFormatModal
        show={showExcelFormatModal}
        onHide={handleCloseExcelModal}
        selectedFormat={selectedExcelFormat}
        onFormatSelect={handleExcelFormatSelect}
        onExport={handleExecuteExcelExport}
        loading={exportLoading}
      />

      {/* Modal de reconciliación */}
      <ReconciliationModal
        show={showReconciliationModal}
        onHide={handleCloseReconciliationModal}
        matches={matches}
        orphanItems={orphanItemsForReconciliation}
        selectedMatches={selectedMatches}
        onMatchSelection={handleMatchSelection}
        onSelectAll={handleSelectAllMatches}
        onExecute={handleExecuteReconciliation}
        loading={reconciliationLoading}
      />

      {/* Modal de cambio de subestados */}
      <SubestadoModal
        show={showSubestadoModal}
        onHide={handleCloseSubestadoModal}
        estadosData={estadosData}
        selectedEstado={selectedEstado}
        selectedSubestado={selectedSubestado}
        onEstadoChange={handleEstadoChange}
        onSubestadoChange={handleSubestadoChange}
        onExecute={handleExecuteSubestadoChange}
        selectedGuiasCount={selectedGuiasCount}
        loading={subestadoLoading}
      />
    </SimpleControlGestionLayout>
  );
};

export default ControlSemanal;
